export type AppearancePresetId =
  | 'verde-avicola'
  | 'azul-corporativo'
  | 'terracota-ejecutivo'
  | 'oliva-premium'
  | 'petroleo-ejecutivo'

export interface AppearancePreset {
  id: AppearancePresetId
  name: string
  description: string
  baseColor: string
  preserveCurrentLook?: boolean
}

export interface BrandThemeTokens {
  primary: string
  primaryRgb: string
  primaryForeground: string
  primaryDeep: string
  primaryDeepRgb: string
  chipText: string
  chipSecondaryText: string
  surfaceTint: string
  surfaceTintRgb: string
  accent: string
  accentRgb: string
  accentForeground: string
  glow: string
  glowRgb: string
  hover: string
  hoverRgb: string
  active: string
  activeRgb: string
  ring: string
  ringRgb: string
  sidebar: string
  sidebarRgb: string
  sidebarMid: string
  sidebarMidRgb: string
  sidebarEnd: string
  sidebarEndRgb: string
  sidebarGlow: string
  sidebarGlowRgb: string
  sidebarForeground: string
  buttonShadow: string
}

type Rgb = { r: number; g: number; b: number }
type Hsl = { h: number; s: number; l: number }

export const DEFAULT_APPEARANCE_PRESET_ID: AppearancePresetId = 'verde-avicola'
export const APPEARANCE_STORAGE_KEY = 'avicola_appearance_preset_v1'

export const APPEARANCE_PRESETS: AppearancePreset[] = [
  {
    id: 'verde-avicola',
    name: 'Verde Avícola',
    description: 'La identidad original del sistema, cálida y confiable.',
    baseColor: '#2F7D57',
    preserveCurrentLook: true,
  },
  {
    id: 'azul-corporativo',
    name: 'Azul Corporativo',
    description: 'Sobrio e institucional, pensado para operación y control.',
    baseColor: '#2F5F8C',
  },
  {
    id: 'terracota-ejecutivo',
    name: 'Terracota Ejecutivo',
    description: 'Cálido, premium y con fuerte personalidad de marca.',
    baseColor: '#9A5A3C',
  },
  {
    id: 'oliva-premium',
    name: 'Oliva Premium',
    description: 'Natural y editorial, con identidad agro elegante.',
    baseColor: '#5E7A32',
  },
  {
    id: 'petroleo-ejecutivo',
    name: 'Petróleo Ejecutivo',
    description: 'Moderno y técnico, con presencia ejecutiva.',
    baseColor: '#2D6F6A',
  },
]

const SURFACE_BG = '#fffdf8'

export function getAppearancePresetById(id?: string | null): AppearancePreset {
  return (
    APPEARANCE_PRESETS.find((preset) => preset.id === id) ??
    APPEARANCE_PRESETS.find((preset) => preset.id === DEFAULT_APPEARANCE_PRESET_ID)!
  )
}

export function isAppearancePresetId(id?: string | null): id is AppearancePresetId {
  return APPEARANCE_PRESETS.some((preset) => preset.id === id)
}

export function deriveBrandThemeTokens(preset: AppearancePreset): BrandThemeTokens {
  if (preset.preserveCurrentLook) {
    return {
      primary: '#2F7D57',
      primaryRgb: '47, 125, 87',
      primaryForeground: '#FFFFFF',
      primaryDeep: '#174B31',
      primaryDeepRgb: '23, 75, 49',
      chipText: '#274133',
      chipSecondaryText: '#173B2A',
      surfaceTint: '#F2F8F0',
      surfaceTintRgb: '242, 248, 240',
      accent: '#B7CF4A',
      accentRgb: '183, 207, 74',
      accentForeground: '#31432D',
      glow: '#C9DA75',
      glowRgb: '201, 218, 117',
      hover: '#3A895F',
      hoverRgb: '58, 137, 95',
      active: '#3F8F62',
      activeRgb: '63, 143, 98',
      ring: '#7AA589',
      ringRgb: '122, 165, 137',
      sidebar: '#1F5A3F',
      sidebarRgb: '31, 90, 63',
      sidebarMid: '#275F44',
      sidebarMidRgb: '39, 95, 68',
      sidebarEnd: '#173B2A',
      sidebarEndRgb: '23, 59, 42',
      sidebarGlow: '#B7CF4A',
      sidebarGlowRgb: '183, 207, 74',
      sidebarForeground: '#F7F4EC',
      buttonShadow: '0 12px 22px rgba(47, 125, 87, 0.18)',
    }
  }

  const base = hexToRgb(preset.baseColor)
  const baseHsl = rgbToHsl(base)
  const primaryDeep = hslToRgb({
    h: baseHsl.h,
    s: clamp(baseHsl.s * 0.95, 0.22, 0.58),
    l: clamp(baseHsl.l - 0.22, 0.18, 0.34),
  })
  const accent = hslToRgb({
    h: baseHsl.h,
    s: clamp(baseHsl.s * 0.58, 0.16, 0.42),
    l: clamp(baseHsl.l + 0.24, 0.56, 0.74),
  })
  const surfaceTint = mixRgb(accent, hexToRgb(SURFACE_BG), 0.86)
  const glow = mixRgb(accent, hexToRgb('#FFFFFF'), 0.18)
  const hover = hslToRgb({
    h: baseHsl.h,
    s: clamp(baseHsl.s * 0.98, 0.2, 0.68),
    l: clamp(baseHsl.l + 0.08, 0.34, 0.56),
  })
  const active = ensureReadable(mixRgb(base, accent, 0.18), SURFACE_BG, primaryDeep)
  const ring = ensureReadable(mixRgb(base, hexToRgb('#FFFFFF'), 0.35), SURFACE_BG, hover)
  const sidebar = hslToRgb({
    h: baseHsl.h,
    s: clamp(baseHsl.s * 0.84, 0.18, 0.52),
    l: clamp(baseHsl.l - 0.18, 0.2, 0.34),
  })
  const sidebarMid = mixRgb(sidebar, base, 0.16)
  const sidebarEnd = hslToRgb({
    h: rgbToHsl(sidebar).h,
    s: clamp(rgbToHsl(sidebar).s * 0.92, 0.16, 0.5),
    l: clamp(rgbToHsl(sidebar).l - 0.1, 0.14, 0.28),
  })
  const sidebarGlow = mixRgb(accent, base, 0.12)
  const chipText = ensureReadable(primaryDeep, SURFACE_BG, hexToRgb('#243126'))
  const chipSecondaryText = ensureReadable(sidebarEnd, SURFACE_BG, chipText)
  const accentForeground = ensureReadable(primaryDeep, rgbToHex(accent), chipText)

  return {
    primary: rgbToHex(base),
    primaryRgb: toRgbVar(base),
    primaryForeground: pickReadableForeground(base),
    primaryDeep: rgbToHex(primaryDeep),
    primaryDeepRgb: toRgbVar(primaryDeep),
    chipText: rgbToHex(chipText),
    chipSecondaryText: rgbToHex(chipSecondaryText),
    surfaceTint: rgbToHex(surfaceTint),
    surfaceTintRgb: toRgbVar(surfaceTint),
    accent: rgbToHex(accent),
    accentRgb: toRgbVar(accent),
    accentForeground: rgbToHex(accentForeground),
    glow: rgbToHex(glow),
    glowRgb: toRgbVar(glow),
    hover: rgbToHex(hover),
    hoverRgb: toRgbVar(hover),
    active: rgbToHex(active),
    activeRgb: toRgbVar(active),
    ring: rgbToHex(ring),
    ringRgb: toRgbVar(ring),
    sidebar: rgbToHex(sidebar),
    sidebarRgb: toRgbVar(sidebar),
    sidebarMid: rgbToHex(sidebarMid),
    sidebarMidRgb: toRgbVar(sidebarMid),
    sidebarEnd: rgbToHex(sidebarEnd),
    sidebarEndRgb: toRgbVar(sidebarEnd),
    sidebarGlow: rgbToHex(sidebarGlow),
    sidebarGlowRgb: toRgbVar(sidebarGlow),
    sidebarForeground: '#F7F4EC',
    buttonShadow: `0 12px 22px rgba(${toRgbVar(base)}, 0.18)`,
  }
}

export function applyBrandThemeTokens(
  tokens: BrandThemeTokens,
  vuetify: { theme?: { themes?: { value: Record<string, any> }; global?: { name: { value: string } } } }
) {
  applyBrandThemeToDocument(tokens)
  applyBrandThemeToVuetify(tokens, vuetify)
}

export function applyBrandThemeToDocument(tokens: BrandThemeTokens) {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  const cssVars: Record<string, string> = {
    '--brand-primary': tokens.primary,
    '--brand-primary-rgb': tokens.primaryRgb,
    '--brand-primary-foreground': tokens.primaryForeground,
    '--brand-primary-deep': tokens.primaryDeep,
    '--brand-primary-deep-rgb': tokens.primaryDeepRgb,
    '--brand-chip-text': tokens.chipText,
    '--brand-chip-secondary-text': tokens.chipSecondaryText,
    '--brand-surface-tint': tokens.surfaceTint,
    '--brand-surface-tint-rgb': tokens.surfaceTintRgb,
    '--brand-accent': tokens.accent,
    '--brand-accent-rgb': tokens.accentRgb,
    '--brand-accent-foreground': tokens.accentForeground,
    '--brand-glow': tokens.glow,
    '--brand-glow-rgb': tokens.glowRgb,
    '--brand-hover': tokens.hover,
    '--brand-hover-rgb': tokens.hoverRgb,
    '--brand-active': tokens.active,
    '--brand-active-rgb': tokens.activeRgb,
    '--brand-ring': tokens.ring,
    '--brand-ring-rgb': tokens.ringRgb,
    '--brand-sidebar': tokens.sidebar,
    '--brand-sidebar-rgb': tokens.sidebarRgb,
    '--brand-sidebar-mid': tokens.sidebarMid,
    '--brand-sidebar-mid-rgb': tokens.sidebarMidRgb,
    '--brand-sidebar-end': tokens.sidebarEnd,
    '--brand-sidebar-end-rgb': tokens.sidebarEndRgb,
    '--brand-sidebar-glow': tokens.sidebarGlow,
    '--brand-sidebar-glow-rgb': tokens.sidebarGlowRgb,
    '--brand-sidebar-foreground': tokens.sidebarForeground,
    '--brand-button-shadow': tokens.buttonShadow,
  }

  Object.entries(cssVars).forEach(([key, value]) => root.style.setProperty(key, value))
}

export function applyBrandThemeToVuetify(
  tokens: BrandThemeTokens,
  vuetify: { theme?: { themes?: { value: Record<string, any> }; global?: { name: { value: string } } } }
) {
  const themeName = vuetify.theme?.global?.name.value ?? 'light'
  const activeTheme = vuetify.theme?.themes?.value?.[themeName]
  if (!activeTheme?.colors) return

  activeTheme.colors = {
    ...activeTheme.colors,
    primary: tokens.primary,
    secondary: tokens.sidebar,
    accent: tokens.accent,
    active: tokens.active,
  }
}

function hexToRgb(hex: string): Rgb {
  const normalized = hex.replace('#', '').trim()
  const value =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => `${char}${char}`)
          .join('')
      : normalized

  return {
    r: Number.parseInt(value.slice(0, 2), 16),
    g: Number.parseInt(value.slice(2, 4), 16),
    b: Number.parseInt(value.slice(4, 6), 16),
  }
}

function rgbToHex({ r, g, b }: Rgb): string {
  return `#${[r, g, b]
    .map((value) => clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0'))
    .join('')}`.toUpperCase()
}

function toRgbVar({ r, g, b }: Rgb): string {
  return `${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}`
}

function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rNorm = r / 255
  const gNorm = g / 255
  const bNorm = b / 255
  const max = Math.max(rNorm, gNorm, bNorm)
  const min = Math.min(rNorm, gNorm, bNorm)
  const delta = max - min

  let h = 0
  const l = (max + min) / 2
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

  if (delta !== 0) {
    switch (max) {
      case rNorm:
        h = ((gNorm - bNorm) / delta) % 6
        break
      case gNorm:
        h = (bNorm - rNorm) / delta + 2
        break
      default:
        h = (rNorm - gNorm) / delta + 4
        break
    }
  }

  return {
    h: Math.round((h * 60 + 360) % 360),
    s,
    l,
  }
}

function hslToRgb({ h, s, l }: Hsl): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2

  let rPrime = 0
  let gPrime = 0
  let bPrime = 0

  if (h < 60) {
    rPrime = c
    gPrime = x
  } else if (h < 120) {
    rPrime = x
    gPrime = c
  } else if (h < 180) {
    gPrime = c
    bPrime = x
  } else if (h < 240) {
    gPrime = x
    bPrime = c
  } else if (h < 300) {
    rPrime = x
    bPrime = c
  } else {
    rPrime = c
    bPrime = x
  }

  return {
    r: Math.round((rPrime + m) * 255),
    g: Math.round((gPrime + m) * 255),
    b: Math.round((bPrime + m) * 255),
  }
}

function mixRgb(colorA: Rgb, colorB: Rgb, weightToB: number): Rgb {
  const weightA = 1 - weightToB
  return {
    r: colorA.r * weightA + colorB.r * weightToB,
    g: colorA.g * weightA + colorB.g * weightToB,
    b: colorA.b * weightA + colorB.b * weightToB,
  }
}

function pickReadableForeground(color: Rgb, dark = '#183F2A'): string {
  return relativeLuminance(color) > 0.47 ? dark : '#FFFFFF'
}

function ensureReadable(color: Rgb, backgroundHex: string, fallback: Rgb): Rgb {
  const contrast = contrastRatio(color, hexToRgb(backgroundHex))
  return contrast >= 2.8 ? color : fallback
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const transform = (value: number) => {
    const channel = value / 255
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  }

  const [rLum, gLum, bLum] = [transform(r), transform(g), transform(b)]
  return 0.2126 * rLum + 0.7152 * gLum + 0.0722 * bLum
}

function contrastRatio(colorA: Rgb, colorB: Rgb): number {
  const luminanceA = relativeLuminance(colorA)
  const luminanceB = relativeLuminance(colorB)
  const lighter = Math.max(luminanceA, luminanceB)
  const darker = Math.min(luminanceA, luminanceB)
  return (lighter + 0.05) / (darker + 0.05)
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}
